import { SegmentedControl } from "./SegmentedControl";

interface SectionSettingsProps {
  /** Which group of controls to render */
  group: "style" | "details";
  /** Current section props (merged with schema defaults) */
  props: Record<string, unknown>;
  /** Callback to update a single prop */
  updateProp: (propName: string, value: unknown) => void;
  /** Whether to show the density control */
  showDensity?: boolean;
  /** Whether to show the width control */
  showWidth?: boolean;
  /** Whether to show the subheading text-width control */
  showTextWidth?: boolean;
  /** Whether to show the heading level control */
  showHeadingLevel?: boolean;
  /** Section type key (e.g. "Hero") -- used to show type-specific options */
  sectionType?: string;
}

/**
 * Checkbox control matching the inspector's visual style.
 */
function CheckboxControl({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2.5 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-3.5 h-3.5 rounded border-ink-5 text-tide accent-tide cursor-pointer"
      />
      <span className="text-[12px] font-medium text-ink-3 group-hover:text-ink-2 transition-colors">
        {label}
      </span>
    </label>
  );
}

export function SectionSettings({
  group,
  props,
  updateProp,
  showDensity = true,
  showWidth = true,
  showTextWidth = false,
  showHeadingLevel = true,
  sectionType,
}: SectionSettingsProps) {
  if (group === "style") {
    const hasDensity = showDensity;
    const hasWidth = showWidth;
    const hasTextWidth = showTextWidth;

    if (!hasDensity && !hasWidth && !hasTextWidth) return null;

    return (
      <>
        {hasDensity && (
          <SegmentedControl
            label="Density"
            options={[
              { label: "Compact", value: "compact" },
              { label: "Normal", value: "normal" },
              { label: "Spacious", value: "spacious" },
              ...(sectionType === "Hero"
                ? [{ label: "Full height", value: "full" }]
                : []),
            ]}
            value={(props.density as string) || "normal"}
            onChange={(v) => updateProp("density", v)}
          />
        )}
        {hasWidth && (
          <SegmentedControl
            label="Width"
            options={[
              { label: "Contained", value: "contained" },
              { label: "Wide", value: "wide" },
              { label: "Full", value: "full" },
            ]}
            value={(props.width as string) || "contained"}
            onChange={(v) => updateProp("width", v)}
          />
        )}
        {hasTextWidth && (
          <SegmentedControl
            label="Text width"
            options={[
              { label: "Narrow", value: "narrow" },
              { label: "Medium", value: "medium" },
              { label: "Full", value: "full" },
            ]}
            value={(props.textWidth as string) || "medium"}
            onChange={(v) => updateProp("textWidth", v)}
          />
        )}
      </>
    );
  }

  // group === "details"
  return (
    <div className="mb-[18px]">
      {showHeadingLevel && (
        <SegmentedControl
          label="Heading level"
          options={[
            { label: "H1", value: "h1" },
            { label: "H2", value: "h2" },
            { label: "H3", value: "h3" },
          ]}
          value={(props.headingLevel as string) || "h2"}
          onChange={(v) => updateProp("headingLevel", v)}
        />
      )}

      <div className="mb-[7px]">
        <span className="block text-[11px] font-bold tracking-[0.05em] uppercase text-ink-4 mb-[7px]">
          Dividers
        </span>
        <div className="flex flex-col gap-2">
          <CheckboxControl
            label="Top divider"
            checked={!!props.topDivider}
            onChange={(v) => updateProp("topDivider", v)}
          />
          <CheckboxControl
            label="Bottom divider"
            checked={!!props.bottomDivider}
            onChange={(v) => updateProp("bottomDivider", v)}
          />
        </div>
      </div>

      <div className="mt-3">
        <CheckboxControl
          label="Hide on mobile"
          checked={!!props.hideOnMobile}
          onChange={(v) => updateProp("hideOnMobile", v)}
        />
      </div>
    </div>
  );
}
